'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {Download, Box, CheckCircle, ChevronLeft, ChevronRight, Layout, Loader2, Puzzle, RefreshCw, Search, Settings, ShieldCheck, Sparkles, Star, Trash2, X, Zap} from 'lucide-react'
import type {Account} from '@/types'
import {apiGet, apiPost, getAuthUser} from '@/lib/api'
import {useRouter, useSearchParams} from '@/lib/router'
import {getPluginConfigComponent} from '@/plugins/registry'

type Props = {
    account: Account | null
}

type PluginItem = {
    id: string
    name: string
    version: string
    author?: string
    category?: string
    group?: string
    summary?: string
    description: string
    installed: boolean
    enabled: boolean
    runtime?: string
    source?: string
    billingType?: 'free' | 'paid'
    activated?: boolean
    installedVersion?: string
    latestVersion?: string
    updateAvailable?: boolean
    iconUrl?: string
    previewUrls?: string[]
    downloadCount?: number
    ratingAverage?: number
    ratingCount?: number
    updatedAt?: number
    marketMode?: 'private' | 'cloud'
    scopeType?: 'deployment' | 'tenant' | 'user' | 'wxid' | 'account'
    requiresAuth?: boolean
    authorized?: boolean
    platformInstalled?: boolean
    installable?: boolean
    actionLabel?: string
}

type PluginOperationKind = 'install' | 'update' | 'uninstall'
type MarketCategory = 'all' | 'installed'
type SortMode = 'popular' | 'latest'
type PluginOperation = {
    pluginId: string
    kind: PluginOperationKind
    progress: number
    phase: string
}

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))
const previewItemsPerPage = 2

const marketCategories: Array<{key: MarketCategory; label: string}> = [
    {key: 'all', label: '全部'},
    {key: 'installed', label: '已安装'},
]

function pluginOperationLabel(kind: PluginOperationKind) {
    if (kind === 'uninstall') return '卸载中'
    if (kind === 'update') return '更新中'
    return '安装中'
}

function pluginOperationPhase(kind: PluginOperationKind, progress: number) {
    if (kind === 'uninstall') {
        if (progress < 30) return '停止插件运行'
        if (progress < 65) return '移除安装记录'
        if (progress < 90) return '清理插件包'
        if (progress < 100) return '刷新插件列表'
        return '卸载完成'
    }
    if (progress < 25) return '准备插件包'
    if (progress < 58) return kind === 'update' ? '下载更新包' : '下载插件包'
    if (progress < 84) return '安装并启动插件'
    if (progress < 100) return '刷新插件列表'
    return kind === 'update' ? '更新完成' : '安装完成'
}

function pluginVersionLabel(plugin: PluginItem) {
    const version = plugin.installed && plugin.installedVersion ? plugin.installedVersion : plugin.latestVersion || plugin.version
    if (!version) return 'v-'
    return version.trim().toLowerCase().startsWith('v') ? version : `v${version}`
}

function formatVersion(version?: string) {
    const value = (version || '').trim()
    if (!value) return 'v-'
    return value.toLowerCase().startsWith('v') ? value : `v${value}`
}

function pluginLatestVersionLabel(plugin: PluginItem) {
    return formatVersion(plugin.latestVersion || plugin.version)
}

function pluginPriceLabel(plugin: PluginItem) {
    if (plugin.requiresAuth && !plugin.authorized) return '未授权'
    if (plugin.source === 'remote' && plugin.platformInstalled === false) return '待安装'
    return plugin.billingType === 'paid' ? (plugin.activated ? '已激活' : '收费') : '免费'
}

function pluginBillingLabel(plugin: PluginItem) {
    return plugin.billingType === 'paid' ? '收费' : '免费'
}

function pluginSourceLabel(plugin: PluginItem) {
    return plugin.author?.trim() || '官方'
}

function pluginCategoryLabel(plugin: PluginItem) {
    const value = plugin.group || plugin.category
    if (value === 'ai') return 'AI工具'
    if (value === 'ui') return '界面'
    if (value === 'commerce') return '电商'
    if (value === 'seo') return 'SEO'
    if (value === 'utility') return '实用工具'
    return value || '实用工具'
}

function pluginRating(plugin: PluginItem) {
    const rating = typeof plugin.ratingAverage === 'number' && plugin.ratingAverage > 0 ? plugin.ratingAverage : 5
    return Math.max(0, Math.min(5, rating)).toFixed(1)
}

function pluginReviewCount(plugin: PluginItem) {
    return Math.max(0, plugin.ratingCount || 0)
}

function canManageWithoutInstance(plugin: PluginItem) {
    return plugin.marketMode === 'cloud' && plugin.installable !== false
}

function isUnauthorizedPlugin(plugin: PluginItem) {
    return plugin.authorized === false || (plugin.requiresAuth === true && plugin.authorized !== true)
}

function shouldShowAuthLabel(plugin: PluginItem) {
    return plugin.billingType === 'paid' || plugin.requiresAuth === true
}

function canConfigurePlugin(plugin: PluginItem) {
    return plugin.installed && plugin.authorized !== false && !(plugin.billingType === 'paid' && !plugin.activated)
}

function pluginDownloads(plugin: PluginItem) {
    return formatCompactCount(plugin.downloadCount || 0)
}

function formatCompactCount(value: number) {
    if (value >= 10000) return `${(value / 10000).toFixed(value >= 100000 ? 0 : 1)}万`
    if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`
    return String(value)
}

function StarRating({plugin}: {plugin: PluginItem}) {
    return (
        <div className="flex items-center text-sm">
            <div className="mr-1 flex items-center text-yellow-400">
                <Star className="h-4 w-4 fill-current"/>
                <span className="ml-1 font-semibold text-slate-700">{pluginRating(plugin)}</span>
            </div>
            <span className="text-slate-400">({pluginReviewCount(plugin)})</span>
        </div>
    )
}

function PluginGlyph({plugin, large = false, compact = false}: {plugin: PluginItem; large?: boolean; compact?: boolean}) {
    const glyphs = [Sparkles, ShieldCheck, Zap, Puzzle, Box]
    let sum = 0
    for (const char of plugin.id) sum += char.charCodeAt(0)
    const Icon = glyphs[sum % glyphs.length]
    const palettes = ['bg-blue-50 text-blue-500', 'bg-emerald-50 text-emerald-500', 'bg-violet-50 text-violet-500', 'bg-amber-50 text-amber-500', 'bg-rose-50 text-rose-500']
    const sizeClass = large ? 'h-20 w-20 sm:h-24 sm:w-24 rounded-2xl' : compact ? 'h-11 w-11 rounded-xl' : 'h-14 w-14 rounded-xl'
    const iconSize = large ? 52 : compact ? 24 : 30
    if (plugin.iconUrl) {
        return (
            <div className={`flex shrink-0 items-center justify-center overflow-hidden bg-white shadow-sm transition-transform group-hover:scale-105 ${sizeClass}`}>
                <img src={plugin.iconUrl} alt={`${plugin.name} icon`} className="h-full w-full object-cover"/>
            </div>
        )
    }
    return (
        <div className={`flex shrink-0 items-center justify-center shadow-sm transition-transform group-hover:scale-105 ${sizeClass} ${palettes[sum % palettes.length]}`}>
            <Icon size={iconSize}/>
        </div>
    )
}

function MarketInstallButton({
    plugin,
    operation,
    disabled,
    onInstall,
    onUninstall,
    onToggle,
    className = '',
}: {
    plugin: PluginItem
    operation: PluginOperation | null
    disabled: boolean
    onInstall: () => void
    onUninstall: () => void
    onToggle?: (enabled: boolean) => void
    className?: string
}) {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        if (operation || disabled) return
        if (plugin.installed) {
            if (plugin.actionLabel === '启用' && onToggle) {
                onToggle(!plugin.enabled)
            } else {
                onUninstall()
            }
            return
        }
        onInstall()
    }

    if (operation) {
        const progress = Math.max(0, Math.min(100, Math.round(operation.progress)))
        const isUninstalling = operation.kind === 'uninstall'
        return (
            <button
                type="button"
                onClick={(event) => event.stopPropagation()}
                className={`relative flex h-9 min-w-[108px] items-center justify-center gap-1.5 overflow-hidden rounded-lg border px-3 text-sm font-medium transition-all duration-300 ${
                    isUninstalling ? 'border-red-100 bg-red-50 text-red-500' : 'border-blue-100 bg-blue-50 text-blue-500'
                } ${className}`}
                title={operation.phase}
            >
                <div className={`absolute bottom-0 left-0 top-0 transition-all duration-150 ${isUninstalling ? 'bg-red-200/60' : 'bg-blue-200/60'}`} style={{width: `${progress}%`}}/>
                <div className="relative z-10 flex items-center gap-1.5">
                    <Loader2 className="h-4 w-4 animate-spin"/>
                    <span className="font-semibold tabular-nums tracking-wide">
                        {pluginOperationLabel(operation.kind)} {progress}%
                    </span>
                </div>
            </button>
        )
    }

    if (plugin.installed) {
        const isTogglePlugin = plugin.actionLabel === '启用'
        const installedText = isTogglePlugin ? (plugin.enabled ? '已启用' : '已停用') : '已安装'
        const uninstallText = isTogglePlugin ? (plugin.enabled ? '停用' : '启用') : '卸载'
        return (
            <button
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className={`group/btn flex h-9 min-w-[108px] items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:border-red-200 hover:bg-red-50 hover:text-red-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
            >
                <div className="flex items-center gap-1.5 group-hover/btn:hidden">
                    <CheckCircle className="h-4 w-4 text-green-500"/>
                    {installedText}
                </div>
                <div className="hidden items-center gap-1.5 group-hover/btn:flex">
                    <Trash2 className="h-4 w-4"/>
                    {uninstallText}
                </div>
            </button>
        )
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={`flex h-9 min-w-[108px] items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white transition-all duration-300 hover:bg-blue-700 hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
        >
            <Download className="h-4 w-4"/>
            {plugin.actionLabel || '安装'}
        </button>
    )
}

export function PluginMarketView({account}: Props) {
    const router = useRouter()
    const searchParams = useSearchParams()
    const locationSearch = searchParams.toString()
    const wxid = account?.wxid || ''
    const [plugins, setPlugins] = useState<PluginItem[]>([])
    const [loading, setLoading] = useState(false)
    const [remoteError, setRemoteError] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [activeCategory, setActiveCategory] = useState<MarketCategory>('all')
    const [sortMode, setSortMode] = useState<SortMode>('popular')
    const [selectedPluginId, setSelectedPluginId] = useState<string | null>(() => searchParams.get('pluginId'))
    const [detailPluginId, setDetailPluginId] = useState<string | null>(null)
    const [previewImageUrl, setPreviewImageUrl] = useState('')
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0)
    const [operation, setOperation] = useState<PluginOperation | null>(null)
    const isAdmin = getAuthUser()?.role === 'admin'

    useEffect(() => {
        setSelectedPluginId(searchParams.get('pluginId'))
    }, [locationSearch])

    function openPluginSettings(pluginId: string) {
        const params = new URLSearchParams(searchParams)
        params.set('pluginId', pluginId)
        router.push(`/plugin/market?${params.toString()}`)
    }

    function closePluginSettings(replaceHistory = false) {
        const params = new URLSearchParams(searchParams)
        params.delete('pluginId')
        const query = params.toString()
        const path = `/plugin/market${query ? `?${query}` : ''}`
        if (replaceHistory) {
            router.replace(path)
        } else {
            router.push(path)
        }
    }

    const selectedPlugin = useMemo(() => plugins.find((item) => item.id === selectedPluginId) || null, [plugins, selectedPluginId])
    const detailPlugin = useMemo(() => plugins.find((item) => item.id === detailPluginId) || null, [detailPluginId, plugins])
    const detailPreviewUrls = detailPlugin?.previewUrls || []
    const detailPreviewPages = Math.ceil(detailPreviewUrls.length / previewItemsPerPage)
    const detailNeedsActivation = detailPlugin
        ? isAdmin && detailPlugin.installed && detailPlugin.billingType === 'paid' && !detailPlugin.activated && detailPlugin.installable !== false
        : false
    const detailCanAdminInstall = detailPlugin
        ? isAdmin && !detailPlugin.installed && detailPlugin.installable !== false
        : false
    const ConfigView = selectedPluginId ? getPluginConfigComponent(selectedPluginId) : null
    const inPluginSettings = !!selectedPluginId
    const visiblePlugins = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        const filtered = plugins.filter((plugin) => {
            const matchesSearch =
                !query ||
                [plugin.name, plugin.summary || '', plugin.description, plugin.author || '', plugin.id, pluginCategoryLabel(plugin), pluginSourceLabel(plugin), pluginPriceLabel(plugin)].some(
                    (value) => value.toLowerCase().includes(query),
                )
            const matchesCategory = activeCategory === 'all' || (activeCategory === 'installed' && plugin.installed)
            return matchesSearch && matchesCategory
        })
        return [...filtered].sort((left, right) => {
            if (sortMode === 'latest') {
                return (right.updatedAt || 0) - (left.updatedAt || 0) || pluginVersionLabel(right).localeCompare(pluginVersionLabel(left)) || left.name.localeCompare(right.name)
            }
            return (
                (right.downloadCount || 0) - (left.downloadCount || 0) ||
                Number(pluginRating(right)) - Number(pluginRating(left)) ||
                (right.ratingCount || 0) - (left.ratingCount || 0) ||
                left.name.localeCompare(right.name)
            )
        })
    }, [activeCategory, plugins, searchQuery, sortMode])
    const activeCategoryLabel = marketCategories.find((item) => item.key === activeCategory)?.label || '全部'

    useEffect(() => {
        setCurrentPreviewIndex(0)
    }, [detailPluginId])

    useEffect(() => {
        if (currentPreviewIndex >= detailPreviewPages) {
            setCurrentPreviewIndex(Math.max(0, detailPreviewPages - 1))
        }
    }, [currentPreviewIndex, detailPreviewPages])

    const loadPlugins = useCallback(async () => {
        const res = await apiGet<{success: boolean; plugins: PluginItem[]; remoteError?: string}>('/plugins', {silent: true})
        const next = res.plugins || []
        setRemoteError(res.remoteError || '')
        setPlugins(next)
    }, [wxid])

    useEffect(() => {
        setLoading(true)
        loadPlugins()
            .catch(() => setPlugins([]))
            .finally(() => setLoading(false))
    }, [loadPlugins])

    async function runPluginOperation(pluginId: string, kind: PluginOperationKind, task: () => Promise<void>) {
        if (operation) return
        let mounted = true
        setOperation({pluginId, kind, progress: 8, phase: pluginOperationPhase(kind, 8)})
        const timer = window.setInterval(() => {
            if (!mounted) return
            setOperation((current) => {
                if (!current || current.pluginId !== pluginId) return current
                const nextProgress = Math.min(88, current.progress + (current.progress < 45 ? 7 : 3))
                return {...current, progress: nextProgress, phase: pluginOperationPhase(kind, nextProgress)}
            })
        }, 360)
        try {
            await task()
            setOperation({pluginId, kind, progress: 100, phase: pluginOperationPhase(kind, 100)})
            await delay(420)
        } finally {
            mounted = false
            window.clearInterval(timer)
            setOperation(null)
        }
    }

    async function installPlugin(plugin: PluginItem) {
        if (operation || plugin.installable === false || (plugin.platformInstalled === false && !isAdmin)) return
        const kind: PluginOperationKind = plugin.installed && plugin.updateAvailable ? 'update' : 'install'
        try {
            await runPluginOperation(plugin.id, kind, async () => {
                // Plugin installation is deployment-scoped. It must not be tied
                // to the currently selected WeChat account.
                await apiPost(`/plugins/install?pluginId=${encodeURIComponent(plugin.id)}`, {})
                await loadPlugins()
            })
        } catch {
            // api.ts has already surfaced the server reason.
        }
    }

    async function activatePlugin(pluginId: string) {
        const code = window.prompt('请输入插件激活码')
        if (!code?.trim()) return
        await apiPost(`/plugins/${pluginId}/activate`, {code: code.trim()})
        await loadPlugins()
    }

    async function uninstallPlugin(plugin: PluginItem) {
        if (operation || plugin.installable === false) return
        try {
            await runPluginOperation(plugin.id, 'uninstall', async () => {
                await apiPost(`/plugins/uninstall?pluginId=${encodeURIComponent(plugin.id)}`, {})
                if (selectedPluginId === plugin.id) {
                    closePluginSettings(true)
                }
                await loadPlugins()
            })
        } catch {
            // api.ts has already surfaced the server reason.
        }
    }

    async function setPluginEnabled(plugin: PluginItem, enabled: boolean) {
        if (operation || !plugin.installed) return
        setPlugins((current) => current.map((item) => item.id === plugin.id ? {...item, enabled} : item))
        try {
            // Plugin enablement is deployment-scoped, matching the marketplace
            // list and installation records. Do not write an account-level row.
            const action = enabled ? 'enable' : 'disable'
            await apiPost(`/plugins/${action}?pluginId=${encodeURIComponent(plugin.id)}`, {})
            await loadPlugins()
        } catch {
            setPlugins((current) => current.map((item) => item.id === plugin.id ? {...item, enabled: plugin.enabled} : item))
        }
    }

    if (inPluginSettings) {
        return (
            <section className="flex h-full flex-col bg-[#f8fafc]">
                <div className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
                    <div className="flex items-center gap-6">
                        <button
                            className="inline-flex h-9 items-center gap-1.5 text-base font-bold text-slate-600 transition-colors hover:text-slate-800 active:scale-95"
                            onClick={() => closePluginSettings()}
                        >
                            <ChevronLeft className="h-5 w-5 stroke-[3]"/> 返回
                        </button>
                        <div className="h-10 w-px bg-slate-200"/>
                        {selectedPlugin ? (
                            <div className="flex min-w-0 items-center gap-3">
                                <PluginGlyph plugin={selectedPlugin} compact/>
                                <div className="min-w-0">
                                    <h1 className="truncate text-base font-medium text-ink">{selectedPlugin.name}</h1>
                                    <p className="truncate text-xs text-muted">{selectedPlugin.summary || selectedPlugin.description || '插件配置'}</p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {ConfigView ? (
                        <ConfigView account={account} onBack={() => closePluginSettings()}/>
                    ) : (
                        <div className="rounded-lg border border-hair bg-white p-8 text-center text-sm text-muted">当前插件未提供前端配置页</div>
                    )}
                </div>
            </section>
        )
    }

    return (
        <section className="flex h-full flex-col bg-[#F8FAFC] font-sans">
            <header className="sticky top-0 z-30 shrink-0 border-b border-slate-200 bg-white shadow-sm">
                <div className="mx-auto flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2">
                        <div className="rounded-lg bg-blue-600 p-2">
                            <Box className="h-5 w-5 text-white"/>
                        </div>
                        <span className="text-xl font-bold tracking-tight text-slate-800">插件市场</span>
                    </div>

                    <div className="mx-8 hidden max-w-xl flex-1 sm:block">
                        <div className="group relative">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <Search className="h-4 w-4 text-slate-400 transition-colors group-focus-within:text-blue-500"/>
                            </div>
                            <input
                                type="text"
                                placeholder="搜索插件、工具或开发者..."
                                className="block w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm leading-5 text-slate-800 placeholder-slate-400 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                           />
                        </div>
                    </div>

                    <div className="w-[100px]"/>
                </div>
            </header>

            <div className="border-b border-slate-200 bg-white px-4 py-3 sm:hidden">
                <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Search className="h-4 w-4 text-slate-400"/>
                    </div>
                    <input
                        type="text"
                        placeholder="搜索插件..."
                        className="block w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm leading-5 text-slate-800 focus:border-blue-500 focus:outline-none"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                   />
                </div>
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8 md:flex-row">
                    <aside className="w-full shrink-0 md:w-56">
                        <div className="sticky top-8">
                            <h3 className="mb-3 px-3 text-xs font-bold uppercase tracking-wider text-slate-400">探索发现</h3>
                            <nav className="space-y-1">
                                {marketCategories.map((category) => (
                                    <button
                                        key={category.key}
                                        type="button"
                                        onClick={() => setActiveCategory(category.key)}
                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                                            activeCategory === category.key ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                        }`}
                                    >
                                        <span>{category.label}</span>
                                        <ChevronRight className={`h-4 w-4 text-blue-500 transition-opacity ${activeCategory === category.key ? 'opacity-100' : 'opacity-0'}`}/>
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <main className="min-w-0 flex-1">
                        <div className="mb-6 flex items-end justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">{activeCategory === 'all' ? '热门推荐' : activeCategoryLabel}</h1>
                                <p className="mt-1 text-sm text-slate-500">找到 {visiblePlugins.length} 款相关的优质插件</p>
                            </div>

                            <div className="hidden h-9 rounded-lg border border-slate-200 bg-white p-1 shadow-sm sm:flex">
                                <button
                                    type="button"
                                    onClick={() => setSortMode('popular')}
                                    className={`rounded-md px-3 text-sm font-medium transition-colors ${
                                        sortMode === 'popular' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:text-slate-800'
                                    }`}
                                >
                                    最受欢迎
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSortMode('latest')}
                                    className={`rounded-md px-3 text-sm font-medium transition-colors ${sortMode === 'latest' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:text-slate-800'}`}
                                >
                                    最新发布
                                </button>
                            </div>
                        </div>

                        {remoteError ? <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">插件管理后台连接失败：{remoteError}</div> : null}

                        {loading ? (
                            <div className="flex h-56 items-center justify-center gap-2 text-sm text-slate-500">
                                <Loader2 className="h-5 w-5 animate-spin"/> 正在加载插件
                            </div>
                        ) : visiblePlugins.length === 0 ? (
                            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-12 text-center">
                                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                                    <Search className="h-8 w-8 text-slate-300"/>
                                </div>
                                <h3 className="mb-1 text-lg font-medium text-slate-800">未找到相关插件</h3>
                                <p className="text-sm text-slate-500">尝试更换搜索词或分组，以发现更多可能。</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                                {visiblePlugins.map((plugin) => {
                                    const currentOperation = operation?.pluginId === plugin.id ? operation : null
                                    // Installation is deployment-scoped, while a non-admin's
                                    // item may not carry a user-level installed flag. A paid
                                    // plugin that is not activated is always non-configurable.
                                    const needsActivation = plugin.billingType === 'paid' && !plugin.activated
                                    const adminInstallRequired = plugin.platformInstalled === false && !plugin.installed && !isAdmin
                                    const actionsDisabled = !!operation || plugin.installable === false || adminInstallRequired
                                    const showActivation = needsActivation
                                    const showConfig = canConfigurePlugin(plugin)
                                    const showUnauthorized = isUnauthorizedPlugin(plugin) && !(isAdmin && !plugin.installed && plugin.installable !== false)
                                    return (
                                        <div
                                            key={plugin.id}
                                            onClick={() => setDetailPluginId(plugin.id)}
                                            className="group relative flex h-full min-h-[236px] cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:border-blue-300 hover:shadow-lg"
                                        >
                                            <div className="mb-4 flex items-start justify-between">
                                                <PluginGlyph plugin={plugin}/>
                                                <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-400">{pluginBillingLabel(plugin)}</span>
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="mb-1 flex items-center gap-2">
                                                    <h3 className="truncate text-lg font-bold text-slate-800 transition-colors group-hover:text-blue-600">{plugin.name}</h3>
                                                    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                                        {pluginVersionLabel(plugin)}
                                                    </span>
                                                    {plugin.updateAvailable ? (
                                                        <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">有更新</span>
                                                    ) : null}
                                                </div>
                                                <p className="mb-4 line-clamp-2 min-h-10 max-h-10 overflow-hidden text-sm leading-5 text-slate-500">
                                                    {plugin.summary || plugin.description || '插件暂未提供简介'}
                                                </p>
                                            </div>

                                            <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                                                <StarRating plugin={plugin}/>
                                                {showActivation ? (
                                                    <button
                                                        type="button"
                                                        className="flex h-9 items-center justify-center gap-1.5 px-1 text-sm font-medium text-slate-400"
                                                        disabled
                                                    >
                                                        配置
                                                        <Settings className="h-4 w-4"/>
                                                    </button>
                                                ) : showUnauthorized ? (
                                                    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">未授权</span>
                                                ) : showConfig ? (
                                                    <button
                                                        type="button"
                                                        className="flex h-9 items-center justify-center gap-1.5 px-1 text-sm font-medium text-slate-700 transition-colors hover:text-blue-600 active:scale-95"
                                                        disabled={!!operation || (plugin.platformInstalled === false && !isAdmin)}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            openPluginSettings(plugin.id)
                                                        }}
                                                    >
                                                        配置
                                                        <Settings className="h-4 w-4"/>
                                                    </button>
                                                ) : plugin.installable === false ? (
                                                    <MarketInstallButton
                                                        plugin={{...plugin, actionLabel: plugin.installed ? plugin.actionLabel : '安装'}}
                                                        operation={currentOperation}
                                                        disabled
                                                        onInstall={() => installPlugin(plugin)}
                                                        onUninstall={() => uninstallPlugin(plugin)}
                                                        onToggle={(enabled) => setPluginEnabled(plugin, enabled)}
                                                   />
                                                ) : (
                                                    <MarketInstallButton
                                                        plugin={plugin}
                                                        operation={currentOperation}
                                                        disabled={actionsDisabled}
                                                        onInstall={() => installPlugin(plugin)}
                                                        onUninstall={() => uninstallPlugin(plugin)}
                                                        onToggle={(enabled) => setPluginEnabled(plugin, enabled)}
                                                   />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </main>
                </div>
            </div>

            {detailPlugin ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
                    <button type="button" className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm" onClick={() => setDetailPluginId(null)} aria-label="关闭插件详情"/>

                    <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <div className="overflow-y-auto custom-scrollbar p-6 sm:p-8">
                            <div className="mb-6 flex items-start justify-between gap-5">
                                <div className="flex min-w-0 flex-1 gap-5">
                                    <PluginGlyph plugin={detailPlugin} large/>
                                    <div className="min-w-0 flex-1 pt-1">
                                        <div className="mb-2 flex min-w-0 flex-wrap items-center gap-3">
                                            <h2 className="truncate text-2xl font-bold text-slate-900">{detailPlugin.name}</h2>
                                            <span className="flex shrink-0 items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600">
                                                <ShieldCheck className="h-3.5 w-3.5"/> {pluginSourceLabel(detailPlugin)}
                                            </span>
                                            {shouldShowAuthLabel(detailPlugin) && isUnauthorizedPlugin(detailPlugin) && !detailCanAdminInstall ? (
                                                <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">未授权</span>
                                            ) : shouldShowAuthLabel(detailPlugin) && canConfigurePlugin(detailPlugin) ? (
                                                <span className="shrink-0 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">已授权</span>
                                            ) : null}
                                        </div>
                                        <p className="max-w-xl text-sm leading-5 text-slate-500">{detailPlugin.summary || detailPlugin.description || '插件暂未提供简介'}</p>
                                    </div>
                                </div>
                                {detailPlugin.installed && !isUnauthorizedPlugin(detailPlugin) && isAdmin ? (
                                    <label className="mt-2 flex shrink-0 cursor-pointer items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={detailPlugin.enabled}
                                            disabled={!!operation}
                                            onChange={(event) => setPluginEnabled(detailPlugin, event.target.checked)}
                                            className="h-4 w-4 cursor-pointer accent-blue-600"
                                       />
                                        <span className={`text-[15px] font-medium ${detailPlugin.enabled ? 'text-blue-600' : 'text-slate-500'}`}>{detailPlugin.enabled ? '已启用' : '已停用'}</span>
                                    </label>
                                ) : null}
                                <button
                                    type="button"
                                    onClick={() => setDetailPluginId(null)}
                                    className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                                    aria-label="关闭详情"
                                >
                                    <X className="h-5 w-5"/>
                                </button>
                            </div>

                            <div className="mb-6 flex flex-wrap items-center gap-8 border-y border-slate-100 py-5 text-sm sm:gap-12">
                                <div className="flex flex-col">
                                    <span className="mb-1 text-slate-400">评分</span>
                                    <div className="flex items-center gap-1 font-semibold text-slate-700">
                                        {pluginRating(detailPlugin)} <Star className="h-4 w-4 fill-yellow-400 text-yellow-400"/>
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <span className="mb-1 text-slate-400">下载量</span>
                                    <span className="font-semibold text-slate-700">{pluginDownloads(detailPlugin)}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="mb-1 text-slate-400">分组</span>
                                    <span className="font-semibold text-slate-700">{pluginCategoryLabel(detailPlugin)}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="mb-1 text-slate-400">{detailPlugin.installed ? '当前版本' : '最新版本'}</span>
                                    <span className="font-semibold text-slate-700">{pluginVersionLabel(detailPlugin)}</span>
                                </div>
                                <div className="ml-auto flex flex-wrap items-center gap-3">
                                {isUnauthorizedPlugin(detailPlugin) && !detailCanAdminInstall && !(isAdmin && detailPlugin.installed) ? null : (
                                        <>
                                            {canConfigurePlugin(detailPlugin) ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 active:scale-95"
                                                        disabled={!!operation || (detailPlugin.platformInstalled === false && !isAdmin)}
                                                        onClick={() => {
                                                            setDetailPluginId(null)
                                                            openPluginSettings(detailPlugin.id)
                                                        }}
                                                    >
                                                        <Settings className="h-4 w-4"/>
                                                        配置
                                                    </button>
                                                </>
                                            ) : null}
                                            <div className="flex flex-wrap items-center gap-3">
                                                {detailPlugin.installed && isAdmin ? (
                                                    <button
                                                        type="button"
                                                        className="flex h-9 min-w-[108px] items-center justify-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                                        disabled={!!operation || detailPlugin.installable === false}
                                                        onClick={() => uninstallPlugin(detailPlugin)}
                                                    >
                                                        <Trash2 className="h-4 w-4"/>
                                                        卸载
                                                    </button>
                                                ) : detailPlugin.installed ? null : detailPlugin.installable === false ? (
                                                    <MarketInstallButton
                                                        plugin={{...detailPlugin, actionLabel: '安装'}}
                                                        operation={operation?.pluginId === detailPlugin.id ? operation : null}
                                                        disabled
                                                        onInstall={() => installPlugin(detailPlugin)}
                                                        onUninstall={() => uninstallPlugin(detailPlugin)}
                                                        onToggle={(enabled) => setPluginEnabled(detailPlugin, enabled)}
                                                   />
                                                ) : detailNeedsActivation && detailPlugin.actionLabel !== '已可用' ? (
                                                    <button type="button" className="btn-secondary h-9 min-w-[60px] px-3 text-sm" disabled>
                                                        {detailNeedsActivation ? '待激活' : detailPlugin.actionLabel || pluginPriceLabel(detailPlugin)}
                                                    </button>
                                                ) : (
                                                    <MarketInstallButton
                                                        plugin={detailPlugin}
                                                        operation={operation?.pluginId === detailPlugin.id ? operation : null}
                                                        disabled={!!operation || (detailPlugin.platformInstalled === false && !isAdmin)}
                                                        onInstall={() => installPlugin(detailPlugin)}
                                                        onUninstall={() => uninstallPlugin(detailPlugin)}
                                                        onToggle={(enabled) => setPluginEnabled(detailPlugin, enabled)}
                                                   />
                                                )}
                                                {detailNeedsActivation ? (
                                                    <button type="button" className="btn-secondary h-9 px-3" disabled={!!operation} onClick={() => activatePlugin(detailPlugin.id)}>
                                                        激活插件
                                                    </button>
                                                ) : null}
                                                {isAdmin && detailPlugin.updateAvailable && detailPlugin.installable !== false && !detailNeedsActivation ? (
                                                    <button
                                                        type="button"
                                                        className="flex h-9 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                                                        disabled={!!operation}
                                                        onClick={() => installPlugin(detailPlugin)}
                                                    >
                                                        <RefreshCw size={16}/> 更新
                                                    </button>
                                                ) : null}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="max-w-none">
                                <h3 className="mb-3 text-lg font-bold text-slate-900">插件介绍</h3>
                                <p className="mb-8 whitespace-pre-wrap leading-relaxed text-slate-600">{detailPlugin.description || detailPlugin.summary || '插件暂未提供介绍。'}</p>

                                <div className="mb-4 flex items-center justify-between">
                                    <h3 className="text-lg font-bold text-slate-900">功能预览</h3>
                                </div>
                                {detailPreviewUrls.length ? (
                                    <div className="group relative px-1">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPreviewIndex((index) => Math.max(0, index - 1))}
                                            disabled={currentPreviewIndex === 0}
                                            aria-label="上一张功能预览"
                                            className={`absolute -left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-100 bg-white/95 text-slate-600 shadow-[0_4px_16px_rgba(0,0,0,0.1)] backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:text-blue-600 active:scale-95 focus:outline-none ${
                                                currentPreviewIndex === 0 ? 'pointer-events-none translate-x-4 opacity-0' : 'translate-x-0 opacity-100'
                                            }`}
                                        >
                                            <ChevronLeft className="h-6 w-6"/>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPreviewIndex((index) => Math.min(detailPreviewPages - 1, index + 1))}
                                            disabled={currentPreviewIndex === detailPreviewPages - 1}
                                            aria-label="下一张功能预览"
                                            className={`absolute -right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-100 bg-white/95 text-slate-600 shadow-[0_4px_16px_rgba(0,0,0,0.1)] backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:text-blue-600 active:scale-95 focus:outline-none ${
                                                currentPreviewIndex === detailPreviewPages - 1 ? 'pointer-events-none -translate-x-4 opacity-0' : 'translate-x-0 opacity-100'
                                            }`}
                                        >
                                            <ChevronRight className="h-6 w-6"/>
                                        </button>

                                        <div className="w-full overflow-hidden py-1">
                                            <div
                                                className="flex transition-transform duration-500 ease-in-out"
                                                style={{transform: `translateX(-${currentPreviewIndex * 100}%)`}}
                                            >
                                                {Array.from({length: detailPreviewPages}).map((_, pageIndex) => (
                                                    <div key={pageIndex} className="grid w-full shrink-0 grid-cols-2 gap-4 px-0.5">
                                                        {detailPreviewUrls
                                                            .slice(pageIndex * previewItemsPerPage, (pageIndex + 1) * previewItemsPerPage)
                                                            .map((previewUrl, itemIndex) => {
                                                                const previewIndex = pageIndex * previewItemsPerPage + itemIndex
                                                                return (
                                                                    <button
                                                                        key={previewUrl}
                                                                        type="button"
                                                                        className="aspect-video w-full cursor-zoom-in select-none overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-0 shadow-sm transition-all hover:border-blue-300 hover:opacity-90 hover:shadow-md"
                                                                        onClick={() => setPreviewImageUrl(previewUrl)}
                                                                        title="点击查看大图"
                                                                    >
                                                                        <img src={previewUrl} alt={`${detailPlugin.name} 功能预览 ${previewIndex + 1}`} className="m-0 block h-full w-full max-w-none object-cover"/>
                                                                    </button>
                                                                )
                                                            })}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {detailPreviewPages > 1 ? (
                                            <div className="mt-3 flex items-center justify-center gap-2" role="tablist" aria-label="功能预览分页">
                                                {Array.from({length: detailPreviewPages}).map((_, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => setCurrentPreviewIndex(index)}
                                                        aria-label={`查看第 ${index + 1} 张功能预览`}
                                                        aria-selected={currentPreviewIndex === index}
                                                        role="tab"
                                                        className={`h-1.5 rounded-full transition-all duration-300 ${currentPreviewIndex === index ? 'w-6 bg-blue-600' : 'w-1.5 bg-slate-300 hover:bg-slate-400'}`}
                                                    />
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="flex aspect-video items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-slate-400 shadow-sm">
                                        <Layout className="mb-2 h-8 w-8 opacity-50"/>
                                        <span className="ml-2 text-sm">暂无功能预览</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {previewImageUrl ? (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
                    <button type="button" className="absolute inset-0 cursor-zoom-out" aria-label="关闭预览" onClick={() => setPreviewImageUrl('')}/>
                    <div className="relative max-h-[88vh] w-full max-w-5xl">
                        <button
                            type="button"
                            className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-2 text-slate-500 shadow-lg transition hover:text-slate-800"
                            aria-label="关闭预览"
                            onClick={() => setPreviewImageUrl('')}
                        >
                            <X className="h-5 w-5"/>
                        </button>
                        <img src={previewImageUrl} alt="功能预览大图" className="max-h-[88vh] w-full rounded-2xl bg-white object-contain shadow-2xl"/>
                    </div>
                </div>
            ) : null}
        </section>
    )
}
